import { useState } from 'react';

/**
 * Initial filter state for the new 5-section filter architecture
 * Used in Phase 2 to set up filter state structure
 *
 * This separates old filters (to be deprecated) from new filters
 * for a gradual migration path
 */
export const createInitialFilterState = () => ({
  departments: [],           // Empty = all departments
  locations: [],             // Empty = all locations
  search: '',
  // OLD FILTERS (to be deprecated gradually)
  //overdueMaintenanceOnly: false,
  //overdueCalibrationOnly: false,
  //expiredWarrantyOnly: false,
  //expiredContractOnly: false,
  //hasComplaintsOnly: false,

  // Date range filters
  installationDateFrom: '',
  installationDateTo: '',
  warrantyDateFrom: '',
  warrantyDateTo: '',
  contractDateFrom: '',
  contractDateTo: '',

  // NEW FILTERS (5-section architecture from Phase 1)
  // SECTION 1: MAINTENANCE STATUS (OR logic - device matches if ANY is true)
  ppmStatus: [],           // ['Overdue', 'DueSoon', 'Current', 'NotSet']
  calibrationStatus: [],   // ['Overdue', 'DueSoon', 'Current', 'NotSet', 'NotRequired']
  complianceStatus: [],    // ['Overdue', 'DueSoon', 'Current', 'NotSet', 'NotRequired']

  // SECTION 2: COVERAGE STATUS (OR logic - applies to both warranty and contract)
  // Consolidated field that applies to both warranty_status and contract_status in backend
  // Note: 'in-house' contract type is excluded from coverage tracking
  coverageStatus: [],      // ['Active & Compliant (>30d)', 'Active - Expiring Soon (≤30d)', 'Expired (0-30d)', 'Expired (30+d)', 'NotCovered']

  // DEPRECATED: Keeping for backward compatibility but no longer used in UI
  //warrantyStatus: [],      // Deprecated - use coverageStatus instead
  //contractStatus: [],      // Deprecated - use coverageStatus instead

  // SECTION 3: DEVICE CHARACTERISTICS (AND logic - device must match ALL criteria)
  criticality: [],          // ['critical', 'major', 'minor']
  serviceContractTypes: [], // ['AMC', 'EPC', 'On-Call', 'Other']
  deviceStatus: [],         // ['active', 'in_transfer'] — narrows list-visible statuses only
  complianceRequired: '',   // '', 'Yes', 'No'
  calibrationRequired: '',  // '', 'Yes', 'No'
  productCostFrom: '',      // Min cost value
  productCostTo: '',        // Max cost value

  // SECTION 4: ISSUES (OR logic for complaints)
  hasActiveComplaints: false, // True = show devices with active complaints only
  hasNoActiveComplaints: false, // True = show devices with NO active complaints
  impactsAvailability: '', // '', 'yes', 'no', 'pending'

  // SECTION 5: SEARCH & DATES (AND logic - already handled above in installationDateFrom/To)
  // Uses: search, installationDateFrom, installationDateTo
});

/**
 * Hook for managing filter state
 * Returns [filters, setFilters] similar to useState
 * Initializes with the new 5-section filter architecture
 *
 * @returns {[Object, Function]} Filter state and setter function
 */
export const useFilterState = () => {
  const [filters, setFilters] = useState(createInitialFilterState());
  return [filters, setFilters];
};

/**
 * Reset filters to initial empty state
 * Call this when user clicks "Clear all filters"
 */
export const getEmptyFilters = () => {
  return createInitialFilterState();
};

/**
 * Merge old filter data with new filter structure
 * Used for backward compatibility during transition from old to new filters
 *
 * @param {Object} oldFilters - Old filter state object
 * @returns {Object} New filter state with merged data
 */
export const migrateFiltersToNewStructure = (oldFilters) => {
  if (!oldFilters) return createInitialFilterState();

  return {
    ...createInitialFilterState(),
    // Preserve old filter values for backward compatibility
    departments: oldFilters.departments || [],
    search: oldFilters.search || '',
    overdueMaintenanceOnly: oldFilters.overdueMaintenanceOnly || false,
    overdueCalibrationOnly: oldFilters.overdueCalibrationOnly || false,
    expiredWarrantyOnly: oldFilters.expiredWarrantyOnly || false,
    expiredContractOnly: oldFilters.expiredContractOnly || false,
    hasComplaintsOnly: oldFilters.hasComplaintsOnly || false,
    installationDateFrom: oldFilters.installationDateFrom || '',
    installationDateTo: oldFilters.installationDateTo || '',
    warrantyDateFrom: oldFilters.warrantyDateFrom || '',
    warrantyDateTo: oldFilters.warrantyDateTo || '',
    contractDateFrom: oldFilters.contractDateFrom || '',
    contractDateTo: oldFilters.contractDateTo || '',
  };
};

export default useFilterState;
